import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { toast } from 'react-hot-toast';
import { Plus, Edit2, Trash2, Power, Send, Eye } from 'lucide-react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { PageHeader, Button, Card, FrequencyBadge, StatusBadge, Modal, LoadingSpinner, SearchBar, Table } from '../components/UI';

export default function Reminders() {
  const navigate = useNavigate();
  const { canManageReports, user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [freq, setFreq] = useState('');
  const [status, setStatus] = useState('');
  const [deptFilter, setDeptFilter] = useState('');
  const [departments, setDepartments] = useState([]);
  const [delTarget, setDelTarget] = useState(null);
  const [delLoading, setDelLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (freq) params.frequency = freq;
      if (status !== '') params.is_active = status;
      if (deptFilter) params.department_id = deptFilter;
      const res = await api.get('/reminders', { params });
      setItems(res.data);
    } catch(e) { toast.error('Failed to load reports'); }
    setLoading(false);
  }, [search, freq, status, deptFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get('/users/departments').then(r => setDepartments(r.data)).catch(() => {});
  }, []);

  async function toggleActive(r) {
    try { await api.patch(`/reminders/${r.id}/toggle`); toast.success(r.is_active ? 'Paused.' : 'Activated.'); load(); }
    catch { toast.error('Failed to toggle.'); }
  }

  async function sendTest(r) {
    try { await api.post(`/reminders/${r.id}/test`); toast.success('Test email sent!'); }
    catch { toast.error('Failed to send test email.'); }
  }

  async function confirmDelete() {
    setDelLoading(true);
    try { await api.delete(`/reminders/${delTarget.id}`); toast.success('Deleted.'); setDelTarget(null); load(); }
    catch { toast.error('Delete failed.'); }
    setDelLoading(false);
  }

  const FREQS = ['daily','weekly','fortnightly','monthly','quarterly','biannually','annually','custom'];

  const columns = [
    {
      header: 'Report', key: 'report_name',
      render: (v, row) => (
        <div>
          <div style={{ fontWeight: 700, color: 'var(--text-primary)', fontSize: 13.5 }}>{v}</div>
          <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 2 }}>{row.department_name || 'No department'}</div>
        </div>
      )
    },
    { header: 'Responsible', key: 'responsible_person', render: (v, row) => <div><div style={{fontSize:13}}>{v}</div><div style={{fontSize:11,color:'var(--text-muted)'}}>{row.responsible_email}</div></div> },
    { header: 'Frequency', key: 'frequency', render: v => <FrequencyBadge frequency={v} /> },
    { header: 'Next Due', key: 'next_due_date', render: v => v ? <span style={{fontSize:12.5,fontWeight:600,fontFamily:'var(--font-mono)'}}>{format(parseISO(v),'MMM d, yyyy')}</span> : '—' },
    { header: 'Status', key: 'is_active', render: v => <StatusBadge isActive={!!v} /> },
    {
      header: 'Actions', key: 'id',
      render: (v, row) => (
        <div style={{ display: 'flex', gap: 6 }}>
          <button title="View" onClick={e => { e.stopPropagation(); navigate(`/reminders/${row.id}`); }} style={{ background: 'var(--primary-ghost)', border: 'none', borderRadius: 7, padding: '5px 8px', cursor: 'pointer', color: 'var(--primary)' }}><Eye size={13} /></button>
          {canManageReports && <>
            <button title="Edit" onClick={e => { e.stopPropagation(); navigate(`/reminders/${row.id}/edit`); }} style={{ background: 'var(--bg-elevated)', border: 'none', borderRadius: 7, padding: '5px 8px', cursor: 'pointer', color: 'var(--text-secondary)' }}><Edit2 size={13} /></button>
            <button title={row.is_active ? 'Pause' : 'Activate'} onClick={e => { e.stopPropagation(); toggleActive(row); }} style={{ background: 'var(--bg-elevated)', border: 'none', borderRadius: 7, padding: '5px 8px', cursor: 'pointer', color: row.is_active ? 'var(--warning)' : 'var(--success)' }}><Power size={13} /></button>
            <button title="Test Email" onClick={e => { e.stopPropagation(); sendTest(row); }} style={{ background: 'var(--bg-elevated)', border: 'none', borderRadius: 7, padding: '5px 8px', cursor: 'pointer', color: 'var(--info)' }}><Send size={13} /></button>
            <button title="Delete" onClick={e => { e.stopPropagation(); setDelTarget(row); }} style={{ background: '#fee2e2', border: 'none', borderRadius: 7, padding: '5px 8px', cursor: 'pointer', color: 'var(--danger)' }}><Trash2 size={13} /></button>
          </>}
        </div>
      )
    },
  ];

  return (
    <div style={{ padding: '32px 36px', maxWidth: 1280, margin: '0 auto' }} className="fade-in">
      <PageHeader
        title="Reports"
        subtitle={`${items.length} report${items.length !== 1 ? 's' : ''} found`}
        action={canManageReports && <Button icon={Plus} onClick={() => navigate('/reminders/new')}>New Report</Button>}
      />

      {/* Filters */}
      <Card style={{ padding: '16px 20px', marginBottom: 20 }}>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <SearchBar value={search} onChange={setSearch} placeholder="Search reports…" style={{ flex: '1 1 220px', minWidth: 180 }} />
          <select value={freq} onChange={e => setFreq(e.target.value)} style={{ width: 'auto', minWidth: 130, flex: '0 0 auto' }}>
            <option value="">All Frequencies</option>
            {FREQS.map(f => <option key={f} value={f}>{f.charAt(0).toUpperCase() + f.slice(1)}</option>)}
          </select>
          <select value={status} onChange={e => setStatus(e.target.value)} style={{ width: 'auto', minWidth: 110, flex: '0 0 auto' }}>
            <option value="">All Status</option>
            <option value="1">Active</option>
            <option value="0">Inactive</option>
          </select>
          {departments.length > 0 && (
            <select value={deptFilter} onChange={e => setDeptFilter(e.target.value)} style={{ width: 'auto', minWidth: 140, flex: '0 0 auto' }}>
              <option value="">All Departments</option>
              {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          )}
        </div>
      </Card>

      <Card style={{ padding: 0 }}>
        {loading ? <LoadingSpinner /> : (
          <Table
            columns={columns}
            data={items}
            onRowClick={row => navigate(`/reminders/${row.id}`)}
            emptyMessage="No reports found. Adjust your filters or create a new report."
          />
        )}
      </Card>

      {/* Delete confirm */}
      <Modal open={!!delTarget} onClose={() => setDelTarget(null)} title="Delete Report"
        footer={<>
          <Button variant="secondary" onClick={() => setDelTarget(null)}>Cancel</Button>
          <Button variant="danger" loading={delLoading} onClick={confirmDelete}>Delete</Button>
        </>}>
        <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>
          Are you sure you want to delete <strong>"{delTarget?.report_name}"</strong>? This will also delete all submission history and reminder logs. This cannot be undone.
        </p>
      </Modal>
    </div>
  );
}
